import { IChangeSetEngineV3, IChangeSetObserverCallBack } from '@src/slide/domain/ui-model/changesetManager/change-set-engine-v3.interface';
import { IBaseModel } from '@src/slide/domain/ui-model/element-modeles/Interface/base/base-model.interface';
import {
    ChangeType, DataModelChange, DataModelInitChange, TableCellUpdateType,
    IShapeProps, IShapeData, ITableData, ITableProps, DataModelDataPatchChange,
} from '@tencent/slide-sdk-model';
import _has from 'lodash/has';

/**
 * sdkData更新监听
 */
export default class ChangesetEngineV3 implements IChangeSetEngineV3 {
    /**
     * 判断某个属性的路径是否在变更信息中
     * @param changesetInfo sdk的changeSet
     * @param path 要判断的属性路径（sdkModel的格式）
     */
    public static hasChangeAtPath(changesetInfo: DataModelChange | null, path: string): boolean {
        if (changesetInfo) {
            const { data } = (changesetInfo as DataModelInitChange<IShapeData, IShapeProps>).diffs;
            if (path.startsWith('txBody')) {
                // 文本自身的变更体现在txBody || 由继承引发的则体现在xxx || 快速样式的文本属性变更
                return _has(data, path) || (changesetInfo.type === ChangeType.PropsInheritance) || _has(data, 'style.fontRef') || data.style === null;
            }
            if (path.startsWith('graphic.graphicData.tbl')) {
                // 表格
                const changeTrTcArr = path.match(/(tr\.|tc\.)(\d+)/g) || [];
                const changeRow = Number(changeTrTcArr[0]?.split('.')[1]);
                const changeCol = Number(changeTrTcArr[1]?.split('.')[1]);
                const changeText = path.indexOf('txBody') > 0;
                const changeCellProperties = path.indexOf('tcPr') > 0;
                const { tableChanges, data } = (changesetInfo as DataModelDataPatchChange<
                    ITableData,
                    ITableProps
                >).diffs;

                // 表头变更
                if (data?.graphic?.graphicData?.tbl) return true;

                // 单元格变更
                if (tableChanges?.length) {
                    return tableChanges.some((e) => {
                        const thisCell = ((e.row === changeRow) || (e.row === 'all')) && ((e.col === changeCol) || (e.col === 'all'));
                        if (!thisCell) {
                            return false;
                        }
                        if (changeText) {
                            return e.updateType?.includes(TableCellUpdateType.Text);
                        }
                        if (changeCellProperties) {
                            return e.updateType?.includes(TableCellUpdateType.CellProperties);
                        }
                        return true;
                    });
                }
                // return true;
            }
            return _has(data, path);
        }
        return false;
    }

    // sdk全量的变更信息
    public changesetInfo: DataModelChange | null;

    // 变更回调
    private changesetCallbacks: IChangeSetObserverCallBack[];

    // 特定路径的变更回调
    private changesetCallbacksPathMap: Map<string, IChangeSetObserverCallBack[]>;

    // model（暂时未用到，留做后续扩展）
    private uiModel: IBaseModel;

    public constructor(uiModel: IBaseModel) {
        this.changesetInfo = null;
        this.changesetCallbacks = [];
        this.changesetCallbacksPathMap = new Map();
        this.uiModel = uiModel;
    }

    /**
     * 加入变更监听
     * @param callback
     */
    public addChangesetObserver(callback: IChangeSetObserverCallBack): void {
        if (!this.changesetCallbacks.includes(callback)) {
            this.changesetCallbacks.push(callback);
        }
    }

    /**
     * 移除变更监听
     * @param callback
     */
    public removeChangesetObserver(callback: IChangeSetObserverCallBack): void {
        if (this.changesetCallbacks.includes(callback)) {
            const index = this.changesetCallbacks.indexOf(callback);
            this.changesetCallbacks.splice(index, 1);
        }
    }

    /**
     * 加入指定路径的变更监听
     * @param path
     * @param callback
     */
    public addObserveForChangeAtPath(path: string, callback: IChangeSetObserverCallBack): void {
        if (callback) {
            let callbacks = this.changesetCallbacksPathMap.get(path);
            if (!callbacks) {
                callbacks = [];
                this.changesetCallbacksPathMap.set(path, callbacks);
            }
            if (!callbacks.includes(callback)) {
                callbacks.push(callback);
            }
        }
    }

    /**
     * 移除指定路径的变更监听
     * @param path
     * @param callback
     */
    public removeObserveForChangeAtPath(path: string, callback: IChangeSetObserverCallBack): void {
        if (callback) {
            const callbacks = this.changesetCallbacksPathMap.get(path);
            if (callbacks?.includes(callback)) {
                const index = callbacks.indexOf(callback);
                callbacks.splice(index, 1);
            }
        }
    }

    /**
     * 存储变更
     * @param changeSetInfo
     */
    public storeChange(changeSetInfo: any): void {
        this.changesetInfo = changeSetInfo;
    }

    /**
     * 通知变更并在通知完后清除
     */
    public notifyChange(): void {
        const notifiedCallbacks: IChangeSetObserverCallBack[] = [];
        const notifiedPathCallbacks: IChangeSetObserverCallBack[] = [];
        const { changesetInfo } = this;
        // 全属性的变更是否有监听，有则通知
        this.changesetCallbacks.forEach((callback) => {
            if (!notifiedCallbacks.includes(callback)) {
                notifiedCallbacks.push(callback);
                try {
                    callback(changesetInfo);
                } catch (e) {
                    window.log.error('[changeset]: changeset callback execute error', e);
                }
            }
        });
        // 指定路径属性的变更是否有监听，有则通知
        this.changesetCallbacksPathMap.forEach((callbacks, path) => {
            if (this.hasChangeAtPath(path)) {
                callbacks.forEach((callback) => {
                    if (!notifiedPathCallbacks.includes(callback)) {
                        notifiedPathCallbacks.push(callback);
                        callback(changesetInfo);
                    }
                });
            }
        });
        this.changesetInfo = null;
    }

    /**
     * 判断对应路径数据是否有变更
     * 兼容旧逻辑，当没有changesetInfo时当做命中处理， 因为这时都是上层手动触发的notifyChange
     * @param path
     */
    public hasChangeAtPath(path: string): boolean {
        if (!this.changesetInfo) {
            return true;
        }
        return ChangesetEngineV3.hasChangeAtPath(this.changesetInfo, path);
    }
}
